/**
 * Tiny, safe markdown renderer.
 * Supports H1/H2/H3, paragraphs, bold (**…**), italic (*…*), inline code (`…`),
 * unordered lists, and horizontal rules. NO raw HTML is rendered — prevents XSS.
 */
const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[c]));

const inline = (text) => {
  const safe = escapeHtml(text);
  return safe
    .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-foreground/10 font-mono text-[0.92em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-foreground">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" class="brand-text underline underline-offset-2 hover:no-underline" target="_blank" rel="noopener noreferrer">$1</a>');
};

export const Markdown = ({ source = "", className = "" }) => {
  const lines = source.split(/\r?\n/);
  const blocks = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      blocks.push({ kind: "ul", items: [...list] });
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*$/.test(line)) { flushList(); continue; }
    if (/^#\s+/.test(line)) { flushList(); blocks.push({ kind: "h1", text: line.replace(/^#\s+/, "") }); continue; }
    if (/^##\s+/.test(line)) { flushList(); blocks.push({ kind: "h2", text: line.replace(/^##\s+/, "") }); continue; }
    if (/^###\s+/.test(line)) { flushList(); blocks.push({ kind: "h3", text: line.replace(/^###\s+/, "") }); continue; }
    if (/^---+\s*$/.test(line)) { flushList(); blocks.push({ kind: "hr" }); continue; }
    if (/^[-*]\s+/.test(line)) { list.push(line.replace(/^[-*]\s+/, "")); continue; }
    flushList();
    blocks.push({ kind: "p", text: line });
  }
  flushList();

  return (
    <div className={`prose prose-invert max-w-none space-y-4 text-sm leading-relaxed ${className}`}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "h1": return <h1 key={i} className="font-heading text-3xl sm:text-4xl mt-2" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
          case "h2": return <h2 key={i} className="font-heading text-xl sm:text-2xl mt-4" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
          case "h3": return <h3 key={i} className="font-heading text-lg mt-3" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
          case "hr": return <hr key={i} className="border-border my-6" />;
          case "ul": return (
            <ul key={i} className="space-y-1.5 list-disc pl-5 text-muted-foreground">
              {b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
            </ul>
          );
          case "p":
          default: return <p key={i} className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
        }
      })}
    </div>
  );
};
